import { Plus, SearchMd, Target03 } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import { useTargetCollaborationsList, useTargetCollaborationsMetrics } from "@/api/collaborations";
import { usePagination } from "@/api/pagination";
import { useSampleRulesCount } from "@/api/sample-rules";
import { useSmartCollabGroups } from "@/api/smart-collab-groups";
import { useTikTokShopConnectionStatus } from "@/api/tiktok-shop";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { PaginationPageMinimalCenter } from "@/components/application/pagination/pagination";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { SmartGroupCard } from "@/components/collaborators/smart-group-collaborator-card";
import { LoadingPageState } from "@/components/loading-page-state";
import { PageLayout } from "@/components/page-layout";
import { SampleRulesDrawer } from "@/components/sample-requests/sample-rules-drawer";
import { useCache } from "@/hooks/use-cache";
import { useClearableDebounce } from "@/hooks/use-debounce";
import { CollabsListEmptyState } from "@/pages/collaborations/collabs-list-empty-state";
import { SampleRulesButton } from "@/pages/operations/sample-requests/sample-requests-controls";
import { SectionHeading } from "./components/SectionHeading";
import { UngroupedCollabsTable } from "./components/UngroupedCollabsTable";
import { CreateCollabModal } from "./modals/create-collab-group-modal";
import { resolveTargetGroupsView } from "./target-groups-view";

export const TargetGroupsPage = () => {
  const [createOpen, setCreateOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { value: debouncedSearch } = useClearableDebounce(search);
  const { data: groups, isLoading: isLoadingGroups } = useSmartCollabGroups(debouncedSearch);
  const ungroupedPagination = usePagination();
  const { data: ungroupedData, isLoading: isLoadingUngrouped } = useTargetCollaborationsList({
    search: debouncedSearch,
    pagination: ungroupedPagination.pagination,
    ungroupedOnly: true,
  });
  const ungroupedMetricRows = useMemo(
    () =>
      (ungroupedData?.collaborations ?? []).map((item) => ({
        key: item.groupId && item.isGroupHeader ? `group-${item.groupId}` : item.collaborationId,
        collaborationIds: item.groupCollaborationIds.length > 0 ? item.groupCollaborationIds : [item.collaborationId],
      })),
    [ungroupedData?.collaborations],
  );
  const { data: ungroupedMetrics } = useTargetCollaborationsMetrics(ungroupedMetricRows);
  const { data: rulesCountData } = useSampleRulesCount();
  const { data: shopStatus } = useTikTokShopConnectionStatus();

  useEffect(() => {
    ungroupedPagination.setPage(1);
  }, [debouncedSearch]);

  const loadedGroupCount = useCache(groups?.length);
  const loadedUngroupedTotal = useCache(ungroupedData?.total);
  const groupCount = loadedGroupCount ?? 0;
  const ungroupedTotal = loadedUngroupedTotal ?? 0;

  // Genuine zero-data — no groups and no ungrouped collabs — or no Shop Access
  // replaces the whole page with the full-bleed first-run empty state. A search
  // keeps the inline sections.
  const shopUnauthorized = shopStatus ? !shopStatus.connected : false;
  const hasNoCollabs = (groups ?? []).length === 0 && (ungroupedData?.total ?? 0) === 0;
  if (!debouncedSearch && (shopUnauthorized || (!isLoadingGroups && !isLoadingUngrouped && hasNoCollabs))) {
    return (
      <>
        <CollabsListEmptyState variant="target" onCreate={() => setCreateOpen(true)} />
        <CreateCollabModal open={createOpen} onOpenChange={setCreateOpen} />
      </>
    );
  }

  const isFirstLoad =
    (isLoadingGroups && loadedGroupCount === undefined) || (isLoadingUngrouped && loadedUngroupedTotal === undefined);
  if (!debouncedSearch && isFirstLoad) return <LoadingPageState />;

  const view = resolveTargetGroupsView({ groupCount, ungroupedTotal });
  const ungroupedPageCount = Math.ceil(ungroupedTotal / ungroupedPagination.pagination.limit);

  return (
    <PageLayout
      title="Target Collaborations"
      subtitle="Invite-only. You choose the creators, and you can set a custom commission rate and offer free samples."
      icon={Target03}
      callToAction={
        <div className="flex items-center gap-3">
          <SampleRulesButton rulesCount={rulesCountData?.total ?? 0} onRulesClick={() => setRulesOpen(true)} />
          <Button
            color="primary"
            size="sm"
            iconLeading={<Plus className="size-4" />}
            onClick={() => setCreateOpen(true)}
          >
            Create Collaboration
          </Button>
        </div>
      }
    >
      <SectionHeading
        title={view.heading}
        count={view.headingCount}
        action={
          <Input
            size="sm"
            inputClassName="text-sm"
            className="w-72"
            value={search}
            onChange={setSearch}
            placeholder="Search Collaboration or Creator name"
            icon={SearchMd}
          />
        }
      />

      <div className="flex flex-col gap-6">
        {view.showGroups && (
          <div className="flex flex-col gap-4">
            {(groups ?? []).map((group) => (
              <SmartGroupCard key={group.id} group={group} />
            ))}
          </div>
        )}

        {view.showDivider && <div className="my-2 h-px w-full bg-border-secondary" />}

        {view.showUngroupedHeading && <SectionHeading title="Ungrouped TikTok Collabs" count={ungroupedTotal} />}

        {(view.showUngrouped || isLoadingUngrouped) && (
          <UngroupedCollabsTable
            items={ungroupedData?.collaborations ?? []}
            metrics={ungroupedMetrics}
            isLoading={isLoadingUngrouped}
          />
        )}

        {view.showUngrouped && ungroupedPageCount > 1 && (
          <PaginationPageMinimalCenter
            page={ungroupedPagination.page}
            total={ungroupedPageCount}
            onPageChange={ungroupedPagination.setPage}
            className="px-4 py-3 md:px-6 md:pt-2 md:pb-2 bg-primary"
          />
        )}

        {!isLoadingGroups && !isLoadingUngrouped && !view.showGroups && !view.showUngrouped && (
          <EmptyState size="md">
            <div className="pb-6">
              <EmptyState.FeaturedIcon color="gray" icon={SearchMd} />
            </div>
            <EmptyState.Content>
              <EmptyState.Title>No collaborations found</EmptyState.Title>
              <EmptyState.Description>No collaboration or creator matches “{debouncedSearch}”.</EmptyState.Description>
            </EmptyState.Content>
          </EmptyState>
        )}
      </div>
      <CreateCollabModal open={createOpen} onOpenChange={setCreateOpen} />
      <SampleRulesDrawer open={rulesOpen} onOpenChange={setRulesOpen} />
    </PageLayout>
  );
};
